/**
 * Settings / Preferences page — 4 tabs: User, Company, Pricing, Subscription.
 * See wireframe: section 14 Settings / Preferences
 *
 * Tab visibility rules:
 *   - User: all roles
 *   - Company: Owner only
 *   - Pricing: Admin + Owner
 *   - Subscription: Owner only
 */

import { getCurrentUser } from '@/api/apiClient';
import type { UserRole } from '@/types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function esc(text: string): string {
  const d = document.createElement('div');
  d.appendChild(document.createTextNode(text));
  return d.innerHTML;
}

function fmt(date: string): string {
  return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function showToast(container: HTMLElement, msg: string, type: 'success' | 'error' = 'success'): void {
  const existing = container.querySelector('.settings-toast');
  existing?.remove();

  const toast = document.createElement('div');
  toast.className = `settings-toast settings-toast--${type}`;
  toast.textContent = msg;
  container.appendChild(toast);
  setTimeout(() => toast.remove(), 3500);
}

// ─── Page factory ──────────────────────────────────────────────────────────────

export function SettingsPage(): HTMLElement {
  const page = document.createElement('div');
  page.className = 'settings-page';

  const user = getCurrentUser();
  const role: UserRole = user?.role ?? 'Member';
  const isOwner = role === 'Owner';
  const isAdminOrOwner = role === 'Owner' || role === 'Admin';

  page.innerHTML = `
    <header class="topbar">
      <a class="topbar-logo" href="#/dashboard">RoofmAIte</a>
      <span class="topbar-title">Settings</span>
      <div class="topbar-actions">
        <a class="btn btn-ghost" href="#/dashboard">Back to Dashboard</a>
      </div>
    </header>

    <main class="settings-main">
      <div class="settings-container">

        <!-- Tab Navigation -->
        <nav class="settings-tabs" role="tablist" aria-label="Settings sections">
          <button class="settings-tab active" role="tab" aria-selected="true"
            data-tab="user" id="tab-user" aria-controls="panel-user">
            User
          </button>
          ${isOwner ? `
          <button class="settings-tab" role="tab" aria-selected="false"
            data-tab="company" id="tab-company" aria-controls="panel-company">
            Company
          </button>` : ''}
          ${isAdminOrOwner ? `
          <button class="settings-tab" role="tab" aria-selected="false"
            data-tab="pricing" id="tab-pricing" aria-controls="panel-pricing">
            Pricing
          </button>` : ''}
          ${isOwner ? `
          <button class="settings-tab" role="tab" aria-selected="false"
            data-tab="subscription" id="tab-subscription" aria-controls="panel-subscription">
            Subscription
          </button>` : ''}
        </nav>

        <!-- Tab Panels -->
        <div class="settings-panels">

          <!-- ── User Preferences ── -->
          <section class="settings-panel" role="tabpanel" id="panel-user" aria-labelledby="tab-user">
            <h2>User Preferences</h2>
            <div class="settings-loading" id="user-loading"><div class="spinner"></div></div>
            <form id="user-prefs-form" hidden>
              <div class="form-group">
                <label class="form-label" for="display-name">Display Name</label>
                <input class="form-input" type="text" id="display-name" name="displayName" maxlength="200" />
              </div>
              <div class="form-group">
                <label class="form-label">Email</label>
                <p class="settings-readonly-value" id="user-email">&ndash;</p>
              </div>
              <div class="form-group">
                <label class="form-label">Units</label>
                <div class="radio-group">
                  <label class="radio-label">
                    <input type="radio" name="unitSystem" value="imperial" />
                    Imperial (feet, inches, x:12)
                  </label>
                  <label class="radio-label">
                    <input type="radio" name="unitSystem" value="metric" />
                    Metric (meters, mm, degrees)
                  </label>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Default Mode</label>
                <div class="radio-group">
                  <label class="radio-label">
                    <input type="radio" name="defaultMode" value="simple" />
                    Simple
                  </label>
                  <label class="radio-label">
                    <input type="radio" name="defaultMode" value="advanced" />
                    Advanced
                  </label>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label" for="grid-size">Grid Size</label>
                <select class="form-select" id="grid-size" name="gridSize">
                  <option value="152">6 inches</option>
                  <option value="305">1 foot</option>
                  <option value="610">2 feet</option>
                  <option value="300">300 mm</option>
                  <option value="500">500 mm</option>
                  <option value="1000">1 meter</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Snapping</label>
                <label class="checkbox-label">
                  <input type="checkbox" id="snap-grid" name="snapToGrid" />
                  Snap to Grid
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" id="snap-vertex" name="snapToVertex" />
                  Snap to Vertex
                </label>
              </div>
              <div class="form-group">
                <label class="form-label" for="snap-radius">Snap Radius (pixels)</label>
                <input class="form-input" type="number" id="snap-radius" name="snapRadius"
                  min="5" max="30" step="1" style="width:100px" />
              </div>
              <div class="settings-form-actions">
                <button type="button" class="btn btn-secondary" id="change-password-btn">Change Password</button>
                <button type="submit" class="btn btn-primary">Save Preferences</button>
              </div>
            </form>

            <!-- Change Password inline form (hidden initially) -->
            <div id="change-password-section" hidden style="margin-top:24px;">
              <h3 style="font-size:15px;margin-bottom:16px;">Change Password</h3>
              <form id="change-password-form">
                <div class="form-group">
                  <label class="form-label" for="new-password">New Password</label>
                  <input class="form-input" type="password" id="new-password"
                    minlength="8" maxlength="128" required autocomplete="new-password" />
                </div>
                <div class="form-group">
                  <label class="form-label" for="confirm-password">Confirm Password</label>
                  <input class="form-input" type="password" id="confirm-password"
                    minlength="8" maxlength="128" required autocomplete="new-password" />
                </div>
                <div class="settings-form-actions">
                  <button type="button" class="btn btn-secondary" id="cancel-password-btn">Cancel</button>
                  <button type="submit" class="btn btn-primary">Update Password</button>
                </div>
              </form>
            </div>
          </section>

          <!-- ── Company Settings (Owner only) ── -->
          ${isOwner ? `
          <section class="settings-panel" role="tabpanel" id="panel-company" aria-labelledby="tab-company" hidden>
            <h2>Company Settings</h2>
            <div class="settings-loading" id="company-loading"><div class="spinner"></div></div>
            <form id="company-form" hidden>
              <div class="form-group">
                <label class="form-label" for="company-name">Company Name</label>
                <input class="form-input" type="text" id="company-name" maxlength="200" />
              </div>
              <div class="form-group">
                <label class="form-label" for="company-region">Region</label>
                <select class="form-select" id="company-region">
                  <option value="US">United States</option>
                  <option value="UK">United Kingdom</option>
                  <option value="AU">Australia</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Company Logo</label>
                <div class="settings-logo-row">
                  <div class="settings-logo-preview" id="logo-preview">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <path d="M3 9l4-4 5 5 3-3 6 6"/>
                    </svg>
                  </div>
                  <label class="btn btn-secondary settings-logo-upload-btn">
                    Upload New
                    <input type="file" accept="image/*" id="logo-upload" hidden />
                  </label>
                  <span class="settings-logo-hint">Used on PDF export cover pages</span>
                </div>
              </div>
              <div class="settings-form-actions">
                <button type="submit" class="btn btn-primary">Save Settings</button>
              </div>
            </form>

            <!-- Team Members (loaded separately) -->
            <div id="team-section" hidden>
              <div class="settings-section-divider"></div>
              <h3 class="settings-section-title">Team Members</h3>
              <div id="team-table-container"></div>
              <div style="margin-top:16px;">
                <button type="button" class="btn btn-secondary" id="invite-btn">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  Invite Team Member
                </button>
              </div>
            </div>

            <!-- Invite form (hidden initially) -->
            <div id="invite-form-section" hidden style="margin-top:24px;">
              <h3 style="font-size:15px;margin-bottom:16px;">Invite Team Member</h3>
              <form id="invite-form">
                <div class="form-group">
                  <label class="form-label" for="invite-name">Name</label>
                  <input class="form-input" type="text" id="invite-name" maxlength="200" required />
                </div>
                <div class="form-group">
                  <label class="form-label" for="invite-email">Email</label>
                  <input class="form-input" type="email" id="invite-email" required />
                </div>
                <div class="form-group">
                  <label class="form-label" for="invite-role">Role</label>
                  <select class="form-select" id="invite-role">
                    <option value="Member">Member</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
                <div class="settings-form-actions">
                  <button type="button" class="btn btn-secondary" id="cancel-invite-btn">Cancel</button>
                  <button type="submit" class="btn btn-primary">Send Invite</button>
                </div>
              </form>
            </div>
          </section>
          ` : ''}

          <!-- ── Material Pricing (Admin/Owner) ── -->
          ${isAdminOrOwner ? `
          <section class="settings-panel" role="tabpanel" id="panel-pricing" aria-labelledby="tab-pricing" hidden>
            <h2>Material Pricing</h2>
            <p class="text-muted" style="margin-bottom:20px;">Prices are shared across your company.</p>
            <div class="settings-pricing-header">
              <label class="form-label" for="pricing-currency">Currency</label>
              <select class="form-select" id="pricing-currency" style="width:220px;">
                <option value="USD">USD - US Dollar</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="AUD">AUD - Australian Dollar</option>
              </select>
            </div>
            <div class="settings-loading" id="pricing-loading"><div class="spinner"></div></div>
            <div id="pricing-table-container" hidden></div>
          </section>
          ` : ''}

          <!-- ── Subscription (Owner only) ── -->
          ${isOwner ? `
          <section class="settings-panel" role="tabpanel" id="panel-subscription" aria-labelledby="tab-subscription" hidden>
            <h2>Subscription</h2>
            <div class="settings-loading" id="subscription-loading"><div class="spinner"></div></div>
            <div id="subscription-content" hidden></div>
          </section>
          ` : ''}

        </div>
      </div>
    </main>
  `;

  _attachHandlers(page, role);
  return page;
}

// ─── Event handlers ────────────────────────────────────────────────────────────

function _attachHandlers(page: HTMLElement, role: UserRole): void {
  const isOwner = role === 'Owner';
  const isAdminOrOwner = role === 'Owner' || role === 'Admin';

  // Tab switching
  const tabs = page.querySelectorAll<HTMLButtonElement>('.settings-tab');
  const panels = page.querySelectorAll<HTMLElement>('.settings-panel');

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      tabs.forEach(t => { t.classList.remove('active'); t.setAttribute('aria-selected', 'false'); });
      panels.forEach(p => { p.hidden = true; });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      page.querySelector<HTMLElement>(`#panel-${tab.dataset['tab']}`)!.hidden = false;

      // Lazy-load tab content on first show
      const tabName = tab.dataset['tab'];
      if (tabName === 'company' && isOwner) _loadCompanyTab(page);
      if (tabName === 'pricing' && isAdminOrOwner) _loadPricingTab(page);
      if (tabName === 'subscription' && isOwner) _loadSubscriptionTab(page);
    });
  });

  // Load user tab immediately (it's the default)
  void _loadUserTab(page);
}

// ─── User tab ─────────────────────────────────────────────────────────────────

async function _loadUserTab(page: HTMLElement): Promise<void> {
  const loading = page.querySelector<HTMLElement>('#user-loading')!;
  const form = page.querySelector<HTMLFormElement>('#user-prefs-form')!;

  try {
    const { tenantApi } = await import('@/api/tenantApi');
    const { authApi } = await import('@/api/authApi');
    const [prefs, me] = await Promise.all([tenantApi.getMyPreferences(), authApi.me()]);

    loading.hidden = true;
    form.hidden = false;

    // Populate
    (form.querySelector<HTMLInputElement>('#display-name')!).value = me.name;
    const emailEl = page.querySelector<HTMLElement>('#user-email')!;
    emailEl.textContent = me.email;

    const unitRadio = form.querySelector<HTMLInputElement>(`input[name="unitSystem"][value="${prefs.unitSystem}"]`);
    if (unitRadio) unitRadio.checked = true;

    const modeRadio = form.querySelector<HTMLInputElement>(`input[name="defaultMode"][value="${prefs.defaultMode}"]`);
    if (modeRadio) modeRadio.checked = true;

    (form.querySelector<HTMLSelectElement>('#grid-size')!).value = String(prefs.gridSize);
    (form.querySelector<HTMLInputElement>('#snap-grid')!).checked = prefs.snapToGrid;
    (form.querySelector<HTMLInputElement>('#snap-vertex')!).checked = prefs.snapToVertex;
    (form.querySelector<HTMLInputElement>('#snap-radius')!).value = String(prefs.snapRadius);

    // Save preferences
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector<HTMLButtonElement>('[type="submit"]')!;
      btn.disabled = true;
      try {
        const data = {
          unitSystem: form.querySelector<HTMLInputElement>('input[name="unitSystem"]:checked')!.value as 'imperial' | 'metric',
          defaultMode: form.querySelector<HTMLInputElement>('input[name="defaultMode"]:checked')!.value as 'simple' | 'advanced',
          gridSize: parseInt(form.querySelector<HTMLSelectElement>('#grid-size')!.value),
          snapToGrid: form.querySelector<HTMLInputElement>('#snap-grid')!.checked,
          snapToVertex: form.querySelector<HTMLInputElement>('#snap-vertex')!.checked,
          snapRadius: parseInt(form.querySelector<HTMLInputElement>('#snap-radius')!.value),
        };
        await tenantApi.updateMyPreferences(data);
        showToast(page, 'Preferences saved.');
      } catch {
        showToast(page, 'Failed to save preferences.', 'error');
      } finally {
        btn.disabled = false;
      }
    });

    // Change password toggle
    const changePwBtn = page.querySelector<HTMLButtonElement>('#change-password-btn')!;
    const cancelPwBtn = page.querySelector<HTMLButtonElement>('#cancel-password-btn')!;
    const pwSection = page.querySelector<HTMLElement>('#change-password-section')!;
    const pwForm = page.querySelector<HTMLFormElement>('#change-password-form')!;

    changePwBtn.addEventListener('click', () => {
      pwSection.hidden = false;
      changePwBtn.hidden = true;
      pwSection.querySelector<HTMLInputElement>('#new-password')?.focus();
    });
    cancelPwBtn.addEventListener('click', () => {
      pwSection.hidden = true;
      changePwBtn.hidden = false;
      pwForm.reset();
    });

    pwForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const newPw = (pwForm.querySelector<HTMLInputElement>('#new-password')!).value;
      const confirmPw = (pwForm.querySelector<HTMLInputElement>('#confirm-password')!).value;
      if (newPw !== confirmPw) {
        showToast(page, 'Passwords do not match.', 'error');
        return;
      }
      const btn = pwForm.querySelector<HTMLButtonElement>('[type="submit"]')!;
      btn.disabled = true;
      try {
        const { authApi: authApiInner } = await import('@/api/authApi');
        await authApiInner.resetPassword('', newPw);
        showToast(page, 'Password updated.');
        pwSection.hidden = true;
        changePwBtn.hidden = false;
        pwForm.reset();
      } catch {
        showToast(page, 'Failed to update password.', 'error');
      } finally {
        btn.disabled = false;
      }
    });

  } catch {
    loading.innerHTML = '<p class="text-danger">Failed to load preferences.</p>';
  }
}

// ─── Company tab ──────────────────────────────────────────────────────────────

let _companyLoaded = false;

async function _loadCompanyTab(page: HTMLElement): Promise<void> {
  if (_companyLoaded) return;
  _companyLoaded = true;

  const loading = page.querySelector<HTMLElement>('#company-loading')!;
  const form = page.querySelector<HTMLFormElement>('#company-form')!;
  const teamSection = page.querySelector<HTMLElement>('#team-section')!;

  try {
    const { tenantApi } = await import('@/api/tenantApi');
    const [tenant, users] = await Promise.all([tenantApi.getTenant(), tenantApi.listUsers()]);

    loading.hidden = true;
    form.hidden = false;
    teamSection.hidden = false;

    // Populate form
    (form.querySelector<HTMLInputElement>('#company-name')!).value = tenant.name;
    (form.querySelector<HTMLSelectElement>('#company-region')!).value = tenant.region;

    // Save company settings
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector<HTMLButtonElement>('[type="submit"]')!;
      btn.disabled = true;
      try {
        await tenantApi.updateTenant({
          name: (form.querySelector<HTMLInputElement>('#company-name')!).value,
          region: (form.querySelector<HTMLSelectElement>('#company-region')!).value as 'US' | 'UK' | 'AU',
        });
        showToast(page, 'Company settings saved.');
      } catch {
        showToast(page, 'Failed to save company settings.', 'error');
      } finally {
        btn.disabled = false;
      }
    });

    // Logo upload (preview only — actual upload would need a separate endpoint)
    const logoUpload = page.querySelector<HTMLInputElement>('#logo-upload')!;
    const logoPreview = page.querySelector<HTMLElement>('#logo-preview')!;
    logoUpload.addEventListener('change', () => {
      const file = logoUpload.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const src = ev.target?.result as string;
        logoPreview.innerHTML = `<img src="${esc(src)}" alt="Company logo" style="max-width:80px;max-height:60px;object-fit:contain;border-radius:4px;" />`;
      };
      reader.readAsDataURL(file);
    });

    // Render team table
    _renderTeamTable(page, users);

    // Invite flow
    const inviteBtn = page.querySelector<HTMLButtonElement>('#invite-btn')!;
    const cancelInviteBtn = page.querySelector<HTMLButtonElement>('#cancel-invite-btn')!;
    const inviteSection = page.querySelector<HTMLElement>('#invite-form-section')!;
    const inviteForm = page.querySelector<HTMLFormElement>('#invite-form')!;

    inviteBtn.addEventListener('click', () => {
      inviteSection.hidden = false;
      page.querySelector<HTMLInputElement>('#invite-name')?.focus();
    });
    cancelInviteBtn.addEventListener('click', () => {
      inviteSection.hidden = true;
      inviteForm.reset();
    });

    inviteForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = inviteForm.querySelector<HTMLButtonElement>('[type="submit"]')!;
      btn.disabled = true;
      try {
        const newUser = await tenantApi.inviteUser({
          name: (inviteForm.querySelector<HTMLInputElement>('#invite-name')!).value,
          email: (inviteForm.querySelector<HTMLInputElement>('#invite-email')!).value,
          role: (inviteForm.querySelector<HTMLSelectElement>('#invite-role')!).value as 'Admin' | 'Member',
        });
        showToast(page, `Invite sent to ${newUser.email}.`);
        inviteSection.hidden = true;
        inviteForm.reset();
        // Refresh team list
        const updatedUsers = await tenantApi.listUsers();
        _renderTeamTable(page, updatedUsers);
      } catch {
        showToast(page, 'Failed to send invite.', 'error');
      } finally {
        btn.disabled = false;
      }
    });

  } catch {
    loading.innerHTML = '<p class="text-danger">Failed to load company settings.</p>';
  }
}

function _renderTeamTable(page: HTMLElement, users: import('@/types').UserDto[]): void {
  const container = page.querySelector<HTMLElement>('#team-table-container')!;
  const currentUser = getCurrentUser();

  if (users.length === 0) {
    container.innerHTML = '<p class="text-muted">No team members yet.</p>';
    return;
  }

  container.innerHTML = `
    <table class="settings-team-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Role</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        ${users.map(u => `
          <tr data-user-id="${esc(u.id)}">
            <td>${esc(u.name)}</td>
            <td class="text-muted">${esc(u.email)}</td>
            <td>
              <select class="form-select team-role-select" style="width:auto;padding:4px 8px;"
                ${u.id === currentUser?.sub ? 'disabled' : ''}>
                <option value="Owner" ${u.role === 'Owner' ? 'selected' : ''}>Owner</option>
                <option value="Admin" ${u.role === 'Admin' ? 'selected' : ''}>Admin</option>
                <option value="Member" ${u.role === 'Member' ? 'selected' : ''}>Member</option>
              </select>
            </td>
            <td>
              ${u.id !== currentUser?.sub ? `
                <button class="btn btn-ghost btn-sm team-remove-btn" data-user-id="${esc(u.id)}">
                  Remove
                </button>` : ''}
            </td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;

  // Role change
  container.querySelectorAll<HTMLSelectElement>('.team-role-select').forEach(select => {
    select.addEventListener('change', async () => {
      const row = select.closest<HTMLElement>('[data-user-id]')!;
      const userId = row.dataset['userId']!;
      try {
        const { tenantApi } = await import('@/api/tenantApi');
        await tenantApi.updateUser(userId, { role: select.value as import('@/types').UserRole });
        showToast(page, 'Role updated.');
      } catch {
        showToast(page, 'Failed to update role.', 'error');
      }
    });
  });

  // Remove user
  container.querySelectorAll<HTMLButtonElement>('.team-remove-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const userId = btn.dataset['userId']!;
      if (!confirm('Remove this team member?')) return;
      try {
        const { tenantApi } = await import('@/api/tenantApi');
        await tenantApi.removeUser(userId);
        showToast(page, 'Team member removed.');
        const updatedUsers = await tenantApi.listUsers();
        _renderTeamTable(page, updatedUsers);
      } catch {
        showToast(page, 'Failed to remove team member.', 'error');
      }
    });
  });
}

// ─── Pricing tab ──────────────────────────────────────────────────────────────

let _pricingLoaded = false;

async function _loadPricingTab(page: HTMLElement): Promise<void> {
  if (_pricingLoaded) return;
  _pricingLoaded = true;

  const loading = page.querySelector<HTMLElement>('#pricing-loading')!;
  const container = page.querySelector<HTMLElement>('#pricing-table-container')!;
  const currencySelect = page.querySelector<HTMLSelectElement>('#pricing-currency')!;

  try {
    const { materialsApi } = await import('@/api/materialsApi');
    const [materials, existingPrices] = await Promise.all([
      materialsApi.list(),
      materialsApi.getPrices(),
    ]);

    const priceMap: Record<string, { price: number; currency: string }> = {};
    for (const p of existingPrices) {
      priceMap[p.materialId] = { price: p.unitPrice, currency: p.currency };
    }

    // Detect currency from first existing price
    const firstPrice = existingPrices[0];
    if (firstPrice) {
      currencySelect.value = firstPrice.currency;
    }

    loading.hidden = true;
    container.hidden = false;

    _renderPricingTable(page, materials, priceMap, currencySelect.value);

    currencySelect.addEventListener('change', () => {
      _renderPricingTable(page, materials, priceMap, currencySelect.value);
    });

  } catch {
    loading.innerHTML = '<p class="text-danger">Failed to load materials.</p>';
  }
}

function _renderPricingTable(
  page: HTMLElement,
  materials: import('@/types').MaterialDto[],
  priceMap: Record<string, { price: number; currency: string }>,
  currency: string,
): void {
  const container = page.querySelector<HTMLElement>('#pricing-table-container')!;
  const currencySymbol = currency === 'GBP' ? '£' : currency === 'AUD' ? 'A$' : '$';

  container.innerHTML = `
    <form id="pricing-form">
      <table class="settings-pricing-table">
        <thead>
          <tr>
            <th class="settings-pricing-th-name">Material</th>
            <th class="settings-pricing-th-unit">Unit</th>
            <th class="settings-pricing-th-price">Price per Unit</th>
          </tr>
        </thead>
        <tbody>
          ${materials.map(m => {
            const existing = priceMap[m.id];
            const price = existing ? existing.price.toFixed(2) : '';
            return `
              <tr>
                <td class="settings-pricing-td-name">${esc(m.name)}</td>
                <td class="settings-pricing-td-unit text-muted">${esc(m.unit)}</td>
                <td class="settings-pricing-td-price">
                  <div class="settings-price-input-wrap">
                    <span class="settings-price-symbol">${currencySymbol}</span>
                    <input class="form-input settings-price-input" type="number" step="0.01" min="0"
                      data-material-id="${esc(m.id)}"
                      placeholder="0.00"
                      value="${price}" />
                  </div>
                </td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
      <div class="settings-form-actions">
        <button type="submit" class="btn btn-primary">Save Prices</button>
      </div>
    </form>
  `;

  const form = container.querySelector<HTMLFormElement>('#pricing-form')!;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector<HTMLButtonElement>('[type="submit"]')!;
    btn.disabled = true;
    const currencySelect = page.querySelector<HTMLSelectElement>('#pricing-currency')!;
    const curr = currencySelect.value;

    try {
      const { materialsApi } = await import('@/api/materialsApi');
      const inputs = form.querySelectorAll<HTMLInputElement>('.settings-price-input');
      const saves: Promise<unknown>[] = [];

      inputs.forEach(input => {
        const materialId = input.dataset['materialId']!;
        const val = parseFloat(input.value);
        if (!isNaN(val) && val > 0) {
          saves.push(materialsApi.upsertPrice({ materialId, unitPrice: val, currency: curr }));
          // Update local price map
          priceMap[materialId] = { price: val, currency: curr };
        }
      });

      await Promise.all(saves);
      showToast(page, 'Prices saved.');
    } catch {
      showToast(page, 'Failed to save prices.', 'error');
    } finally {
      btn.disabled = false;
    }
  });
}

// ─── Subscription tab ─────────────────────────────────────────────────────────

let _subscriptionLoaded = false;

async function _loadSubscriptionTab(page: HTMLElement): Promise<void> {
  if (_subscriptionLoaded) return;
  _subscriptionLoaded = true;

  const loading = page.querySelector<HTMLElement>('#subscription-loading')!;
  const content = page.querySelector<HTMLElement>('#subscription-content')!;

  try {
    const { tenantApi } = await import('@/api/tenantApi');
    const tenant = await tenantApi.getTenant();
    const currentTier = tenant.subscriptionTier;

    // Simulated renewal date (would come from a billing API in production)
    const renewal = fmt(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString());

    const tiers = [
      {
        id: 'Basic' as const,
        price: '$49/mo',
        features: [
          '10 projects',
          'PDF export',
          'Simple mode',
          '1 user',
        ],
      },
      {
        id: 'Professional' as const,
        price: '$99/mo',
        features: [
          'Unlimited projects',
          'All exports',
          'Both modes',
          '5 users',
          'Multi-building',
        ],
      },
      {
        id: 'Enterprise' as const,
        price: '$149/mo',
        features: [
          'Unlimited projects',
          'All exports',
          'Both modes',
          '25 users',
          'Multi-building',
          'DWG export',
          'Custom branding',
          'Priority support',
        ],
      },
    ];

    loading.hidden = true;
    content.hidden = false;

    content.innerHTML = `
      <div class="settings-sub-current">
        <div class="settings-sub-current-row">
          <span class="settings-sub-label">Current Plan</span>
          <span class="settings-sub-value settings-sub-tier-badge">${esc(currentTier)}</span>
        </div>
        <div class="settings-sub-current-row">
          <span class="settings-sub-label">Status</span>
          <span class="settings-sub-value settings-sub-status--active">Active</span>
        </div>
        <div class="settings-sub-current-row">
          <span class="settings-sub-label">Renewal Date</span>
          <span class="settings-sub-value">${renewal}</span>
        </div>
      </div>

      <div class="settings-tier-grid">
        ${tiers.map(tier => {
          const isCurrent = tier.id === currentTier;
          const isUpgrade = _tierRank(tier.id) > _tierRank(currentTier);
          const isDowngrade = _tierRank(tier.id) < _tierRank(currentTier);
          return `
            <div class="settings-tier-card ${isCurrent ? 'settings-tier-card--current' : ''}">
              <div class="settings-tier-name">${tier.id.toUpperCase()}</div>
              <div class="settings-tier-price">${tier.price}</div>
              ${isCurrent ? '<div class="settings-tier-current-label">Current Plan</div>' : ''}
              <ul class="settings-tier-features">
                ${tier.features.map(f => `<li>${esc(f)}</li>`).join('')}
              </ul>
              <div class="settings-tier-action">
                ${isCurrent
                  ? '<span class="settings-tier-current-btn">Current</span>'
                  : isUpgrade
                    ? `<button class="btn btn-primary btn-sm" data-tier="${tier.id}" data-action="upgrade">Upgrade</button>`
                    : isDowngrade
                      ? `<button class="btn btn-secondary btn-sm" data-tier="${tier.id}" data-action="downgrade">Downgrade</button>`
                      : ''
                }
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;

    // Upgrade/downgrade buttons
    content.querySelectorAll<HTMLButtonElement>('[data-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const tier = btn.dataset['tier']!;
        const action = btn.dataset['action']!;
        const confirmed = confirm(`${action === 'upgrade' ? 'Upgrade' : 'Downgrade'} to ${tier} plan?`);
        if (!confirmed) return;
        // In production, would call a billing API
        showToast(page, `Please contact support to ${action} your plan.`);
      });
    });

  } catch {
    loading.innerHTML = '<p class="text-danger">Failed to load subscription details.</p>';
  }
}

function _tierRank(tier: import('@/types').SubscriptionTier): number {
  const ranks: Record<import('@/types').SubscriptionTier, number> = {
    Basic: 0,
    Professional: 1,
    Enterprise: 2,
  };
  return ranks[tier];
}
